import { useState } from 'react';
import { createPortal } from 'react-dom';
import './Message.css';

function Message({ message, currentUserId, onReply, selectMode, isSelected, onToggleSelect, onPin, isAdmin }) {
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [swiping, setSwiping] = useState(false);
  const [swipeDistance, setSwipeDistance] = useState(0);
  const [showImageModal, setShowImageModal] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  if (message.isSystemMessage) {
    return (
      <div className="system-message">
        <span>{message.text}</span>
      </div>
    );
  }

  const isOwnMessage = message.userId === currentUserId;
  const minSwipeDistance = 50;

  const handleImageClick = (e) => {
    e.stopPropagation();
    // Prevent opening if we just closed the modal
    if (isClosing) return;
    setShowImageModal(true);
  };

  const handleModalClose = (e) => {
    if (e.target === e.currentTarget) {
      setIsClosing(true);
      setShowImageModal(false);
      // Reset the closing flag after a short delay
      setTimeout(() => {
        setIsClosing(false);
      }, 300);
    }
  };

  const onTouchStart = (e) => {
    if (selectMode) return; // Disable swipe in select mode
    setTouchEnd(0);
    setTouchStart(e.targetTouches[0].clientX);
    setSwiping(true);
  };

  const onTouchMove = (e) => {
    const currentTouch = e.targetTouches[0].clientX;
    setTouchEnd(currentTouch);
    
    // Calculate swipe distance
    const distance = isOwnMessage 
      ? touchStart - currentTouch  // Swipe left for own messages
      : currentTouch - touchStart; // Swipe right for other messages
    
    // Limit swipe distance
    if (distance > 0 && distance < 100) {
      setSwipeDistance(distance);
    }
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) {
      setSwiping(false);
      setSwipeDistance(0);
      return;
    }

    const distance = isOwnMessage 
      ? touchStart - touchEnd
      : touchEnd - touchStart;

    if (distance > minSwipeDistance) {
      onReply(message);
    }

    setSwiping(false);
    setSwipeDistance(0);
  };

  // Highlight mentions in message text
  const renderMessageText = (text) => {
    if (!message.mentions || message.mentions.length === 0) {
      return text;
    }

    const parts = [];
    let remainingText = text;
    
    // Sort mentions by their position in text
    const mentionsWithPos = message.mentions.map(m => {
      const mentionStr = `@${m.userName}`;
      const pos = remainingText.indexOf(mentionStr);
      return { ...m, mentionStr, pos };
    }).filter(m => m.pos !== -1).sort((a, b) => a.pos - b.pos);

    let lastIndex = 0;
    mentionsWithPos.forEach((mention, index) => {
      // Add text before mention
      if (mention.pos > lastIndex) {
        parts.push(remainingText.substring(lastIndex, mention.pos));
      }
      
      // Add mention with highlight
      const isMentioned = mention.userId === currentUserId;
      parts.push(
        <span 
          key={`mention-${index}`} 
          className={`mention ${isMentioned ? 'mention-me' : ''}`}
        >
          {mention.mentionStr}
        </span>
      );
      
      lastIndex = mention.pos + mention.mentionStr.length;
    });

    // Add remaining text
    if (lastIndex < remainingText.length) {
      parts.push(remainingText.substring(lastIndex));
    }

    return parts.length > 0 ? parts : text;
  };

  const swipeStyle = swiping ? {
    transform: isOwnMessage 
      ? `translateX(-${swipeDistance}px)` 
      : `translateX(${swipeDistance}px)`,
    transition: 'none'
  } : {};

  return (
    <div 
      className={`message ${isOwnMessage ? 'own-message' : 'other-message'} ${swiping ? 'swiping' : ''} ${selectMode ? 'select-mode' : ''} ${isSelected ? 'selected' : ''} ${message.isPinned ? 'pinned-message' : ''}`}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      style={swipeStyle}
      onClick={() => selectMode && onToggleSelect && onToggleSelect(message.id)}
    >
      {selectMode && (
        <div className="message-checkbox">
          <input 
            type="checkbox" 
            checked={isSelected} 
            onChange={() => onToggleSelect && onToggleSelect(message.id)}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
      
      <div className="message-content-wrapper">
        {message.isPinned && (
          <div className="pinned-badge">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M16 12V4h1c.55 0 1-.45 1-1s-.45-1-1-1H7c-.55 0-1 .45-1 1s.45 1 1 1h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z"/>
            </svg>
            <span>Pinned</span>
          </div>
        )}
        
        <div className="message-header">
          <span className="message-author">{message.userName}</span>
          <span className="message-time">
            {message.createdAt?.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>
        
        {message.replyTo && (
          <div className="reply-reference">
            <div className="reply-content">
              <span className="reply-author">{message.replyTo.userName}</span>
              <span className="reply-text">
                {message.replyTo.text.length > 30 
                  ? message.replyTo.text.substring(0, 30) + '...' 
                  : message.replyTo.text}
              </span>
            </div>
          </div>
        )}
        
        {message.hasImage && message.image && (
          <>
            <div 
              className="message-image-container" 
              onClick={handleImageClick}
            >
              <img src={message.image} alt="Shared" className="message-image" />
            </div>
            
            {showImageModal && createPortal(
              <div 
                className="image-modal-overlay" 
                onClick={handleModalClose}
              >
                <div className="image-modal-content">
                  <div className="image-modal-controls">
                    <button 
                      className="image-modal-save" 
                      onClick={(e) => {
                        e.stopPropagation();
                        const link = document.createElement('a');
                        link.href = message.image;
                        link.download = `image-${Date.now()}.png`;
                        link.click();
                      }}
                      title="Save Image"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v12m0 0l-4-4m4 4l4-4M2 17l.621 2.485A2 2 0 004.561 21h14.878a2 2 0 001.94-1.515L22 17" />
                      </svg>
                    </button>
                    <button 
                      className="image-modal-close" 
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsClosing(true);
                        setShowImageModal(false);
                        setTimeout(() => setIsClosing(false), 300);
                      }}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <img src={message.image} alt="Shared" className="image-modal-img" />
                </div>
              </div>,
              document.body
            )}
          </>
        )}
        
        {message.text && (
          <div className="message-text">{renderMessageText(message.text)}</div>
        )}
        
        {!selectMode && (
          <>
            <button 
              className="reply-btn"
              onClick={() => onReply(message)}
              title="Reply to this message"
              aria-label="Reply"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
              </svg>
            </button>
            
            {isAdmin && (
              <button 
                className="pin-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onPin && onPin(message.id);
                }}
                title={message.isPinned ? "Unpin message" : "Pin message"}
                aria-label={message.isPinned ? "Unpin" : "Pin"}
              >
                <svg viewBox="0 0 24 24" fill={message.isPinned ? "currentColor" : "none"} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                </svg>
              </button>
            )}
          </>
        )}

        {swiping && swipeDistance > minSwipeDistance && (
          <div className={`swipe-indicator ${isOwnMessage ? 'swipe-left' : 'swipe-right'}`}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
            </svg>
          </div>
        )}
      </div>
    </div>
  );
}

export default Message;
